import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { Textarea } from "@shared-ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { apiErrorMessage } from "@/lib/api-error";
import {
	createVendor,
	type NearMatch,
	type VendorIdentity,
} from "../services/vendors-api.service";

const TITLE_ID = "add-vendor-title";

interface AddVendorDialogProps {
	onClose: () => void;
	onCreated: (vendor: VendorIdentity, nearMatches: NearMatch[]) => void;
}

/**
 * Admin's own entry point for creating a vendor, `POST /api/v1/admin/vendors`,
 * which runs the same handler and schema as the `POST /api/v1/vendors`
 * designers use.
 *
 * Designers and contractors already create vendors while sourcing materials
 * (see vendors-dashboard.tsx's header comment); this gives an admin the same
 * capability without impersonating one of them. `isVerified` is not on this
 * form -- the create endpoint doesn't accept it at all, and the dashboard's
 * own verified switch is the only way to grant it.
 */
export function AddVendorDialog({ onClose, onCreated }: AddVendorDialogProps) {
	const [name, setName] = useState("");
	const [city, setCity] = useState("");
	const [state, setState] = useState("");
	const [contactName, setContactName] = useState("");
	const [contactPhone, setContactPhone] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [notes, setNotes] = useState("");

	const create = useMutation({
		mutationFn: async () =>
			createVendor({
				name: name.trim(),
				city: city.trim() || null,
				state: state.trim() || null,
				contactName: contactName.trim() || null,
				contactPhone: contactPhone.trim() || null,
				contactEmail: contactEmail.trim() || null,
				notes: notes.trim() || null,
			}),
		onSuccess: ({ vendor, nearMatches }) => onCreated(vendor, nearMatches),
	});

	const canSave = name.trim() !== "" && !create.isPending;

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!create.isPending) onClose();
			}}
			className="grid w-full max-w-lg gap-4 rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					Add vendor
				</h2>
				<p className="text-sm text-muted-foreground">
					This vendor is visible to every designer and contractor on the
					platform. It is created unverified — grant the badge afterward from
					this page if warranted.
				</p>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="vendor-name">Name</Label>
				<Input
					id="vendor-name"
					value={name}
					onChange={(event) => setName(event.target.value)}
				/>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="grid gap-1.5">
					<Label htmlFor="vendor-city">City (optional)</Label>
					<Input
						id="vendor-city"
						value={city}
						onChange={(event) => setCity(event.target.value)}
					/>
				</div>
				<div className="grid gap-1.5">
					<Label htmlFor="vendor-state">State (optional)</Label>
					<Input
						id="vendor-state"
						value={state}
						onChange={(event) => setState(event.target.value)}
					/>
				</div>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="vendor-contact-name">Contact name (optional)</Label>
				<Input
					id="vendor-contact-name"
					value={contactName}
					onChange={(event) => setContactName(event.target.value)}
				/>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="grid gap-1.5">
					<Label htmlFor="vendor-contact-phone">Phone (optional)</Label>
					<Input
						id="vendor-contact-phone"
						value={contactPhone}
						onChange={(event) => setContactPhone(event.target.value)}
					/>
				</div>
				<div className="grid gap-1.5">
					<Label htmlFor="vendor-contact-email">Email (optional)</Label>
					<Input
						id="vendor-contact-email"
						type="email"
						value={contactEmail}
						onChange={(event) => setContactEmail(event.target.value)}
					/>
				</div>
			</div>

			<div className="grid gap-1.5">
				<Label htmlFor="vendor-notes">Notes (optional)</Label>
				<Textarea
					id="vendor-notes"
					value={notes}
					onChange={(event) => setNotes(event.target.value)}
					rows={3}
				/>
			</div>

			{create.isError ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(create.error, "Could not create this vendor.")}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onClose} disabled={create.isPending}>
					Cancel
				</Button>
				<Button onClick={() => create.mutate()} disabled={!canSave}>
					{create.isPending ? "Adding…" : "Add vendor"}
				</Button>
			</div>
		</Dialog>
	);
}
